/**
 * Golf Fore Good: choose a division (entry type), then into the flow.
 *
 * Tapping a division stashes it in FlowContext and routes to /auth (or past
 * it when a JWT is already cached for this embed). Single-division events
 * skip this step: the lone division is picked and the flow advances.
 * Divisions that group show under headings, each with its capacity line;
 * one "Who's playing" list for the whole event sits underneath.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { EmbedClient, DivisionResponse, RegisteredTeam } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { useFlowNavigate } from '@/state/useFlowNavigate';
import { useVocab } from '@/state/vocab';
import { groupDivisions } from './divisionGroups';
import '@/pages/EventPage.css';

interface EventPageProps {
  client: EmbedClient;
}

export function GfgEventPage({ client }: EventPageProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useFlowNavigate();
  const { selectDivision } = useFlow();
  const vocab = useVocab();
  const [divisions, setDivisions] = useState<DivisionResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "Who's playing": teams already registered. Loaded alongside divisions;
  // failures are swallowed (it's supplementary, never blocks registering).
  const [teams, setTeams] = useState<RegisteredTeam[] | null>(null);
  const autoPickedRef = useRef(false);

  useEffect(() => {
    if (!eventId) return;
    client
      .getDivisions(eventId)
      .then(setDivisions)
      .catch((err) => {
        setError(
          err?.response?.status === 404
            ? 'This event is not available through this registration link.'
            : 'Could not load divisions. Please try again.',
        );
      });
  }, [client, eventId]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    client
      .getRegisteredTeams(eventId)
      .then((res) => { if (!cancelled) setTeams(res.teams); })
      .catch(() => { if (!cancelled) setTeams([]); });
    return () => { cancelled = true; };
  }, [client, eventId]);

  const onPickDivision = useCallback(
    (name: string, replace = false) => {
      selectDivision(name);
      const dest = client.isAuthed()
        ? `/events/${eventId}/teams`
        : `/events/${eventId}/auth`;
      navigate(dest, replace ? { replace: true } : undefined);
    },
    [client, eventId, navigate, selectDivision],
  );

  // Single-division events have nothing to choose — auto-select the lone
  // division and advance. `replace: true` keeps this vestigial step out
  // of history so Back returns to the product chooser instead of bouncing
  // forward through the skip again. Ref-guarded so it fires exactly once
  // even under StrictMode's double-invoked effects. Full/closed divisions
  // don't auto-advance — the list renders so the coach sees why.
  useEffect(() => {
    if (!divisions || divisions.length !== 1 || autoPickedRef.current) return;
    if (divisions[0].is_full || divisions[0].is_closed) return;
    autoPickedRef.current = true;
    onPickDivision(divisions[0].name, true);
  }, [divisions, onPickDivision]);

  if (error) {
    return <div role="alert" className="tn-event__error">{error}</div>;
  }

  if (!divisions) {
    return <div className="tn-event__loading">Loading divisions…</div>;
  }

  // Auto-skip in flight for a single-division event — render the loading
  // placeholder rather than flash a one-item list for a frame. A full or
  // closed lone division doesn't auto-skip; fall through to the list so
  // the coach sees the state instead of an infinite spinner.
  if (divisions.length === 1 && !divisions[0].is_full && !divisions[0].is_closed) {
    return <div className="tn-event__loading">Loading…</div>;
  }

  if (divisions.length === 0) {
    return (
      <div className="tn-card tn-event__empty">
        <h2>No {vocab.divisionPlural} yet</h2>
        <p>
          The organizer hasn't published {vocab.divisionPlural} for this event yet.
          Check back soon.
        </p>
      </div>
    );
  }

  return (
    <div className="tn-event">
      <h1 className="tn-event__title">Choose your {vocab.division}</h1>
      {groupDivisions(divisions).map((group) => (
      <section key={group.label ?? 'all'} className="tn-event__group" aria-label={group.label ?? undefined}>
      {group.label ? <h2 className="tn-event__group-label">{group.label}</h2> : null}
      <ul className="tn-event__divisions">
        {group.items.map(({ division: d, display }) => {
          const unavailable = d.is_full || d.is_closed;
          return (
            <li key={d.id} className="tn-event__division-item">
              <button
                type="button"
                className={`tn-event__division${unavailable ? ' tn-event__division--unavailable' : ''}`}
                disabled={unavailable}
                onClick={() => onPickDivision(d.name)}
              >
                <span className="tn-event__division-name">{display}</span>
                {unavailable ? (
                  <span className="tn-event__division-unavailable">
                    {d.is_full ? 'Full' : 'Registration closed'}
                  </span>
                ) : d.capacity != null ? (
                  <span className="tn-event__division-capacity">
                    {d.capacity} {vocab.capacityNoun}{d.capacity === 1 ? '' : 's'} cap
                  </span>
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
      </section>
      ))}
      <WhosPlaying teams={teams} vocab={vocab} />
    </div>
  );
}

/** "Who's playing": every registered entry for the event. */
function WhosPlaying({
  teams,
  vocab,
}: {
  teams: RegisteredTeam[] | null;
  vocab: ReturnType<typeof useVocab>;
}) {
  if (!teams || teams.length === 0) return null;
  return (
    <section className="tn-event__whos-playing" aria-label={`${vocab.entrantPlural} registered`}>
      <h2 className="tn-event__whos-playing-title">
        Who's playing
        <span className="tn-event__whos-playing-count">{teams.length}</span>
      </h2>
      <TeamList teams={teams} />
    </section>
  );
}

function TeamList({ teams }: { teams: RegisteredTeam[] }) {
  const place = (t: RegisteredTeam) => [t.city, t.state].filter(Boolean).join(', ');
  return (
    <ul className="tn-event__teams">
      {teams.map((t) => (
        <li key={t.id} className="tn-event__team">
          {t.logo_url ? (
            <img className="tn-event__team-logo" src={t.logo_url} alt="" loading="lazy" />
          ) : (
            <span className="tn-event__team-logo tn-event__team-logo--placeholder" aria-hidden="true">
              {t.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <span className="tn-event__team-info">
            <span className="tn-event__team-name">{t.name}</span>
            {(place(t) || t.division) && (
              <span className="tn-event__team-meta">
                {[place(t), t.division].filter(Boolean).join(' · ')}
              </span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
